import React, { useState, useEffect, useRef } from 'react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import { useForm } from 'react-hook-form';
import { translateData, getLanguages, getCrops } from '../lib/api';
import { Icon } from '@iconify/react/dist/iconify.js';

const customStyles = `
  .form-grid-container {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 1.5rem !important;
    width: 100% !important;
    margin-bottom: 1.25rem !important;
  }
  .form-control, textarea {
    width: 100% !important;
    border-radius: 8px !important;
    padding: 8px 12px !important;
    border: 1px solid #ced4da !important;
    background: #fff !important;
  }
  .error-message { color: #dc3545; font-size: 0.75rem; }
  .success-message { color: #28a745; text-align: center; margin-bottom: 1rem; }

  .image-upload {
    border: 2px dashed #e2e8f0 !important;
    border-radius: 8px !important;
    padding: 1rem !important;
    text-align: center !important;
    background: #f8fafc !important;
    cursor: pointer !important;
  }

  /* Main Tabs (English / Other Languages) */
  .main-tabs { display: flex; gap: 8px; margin-bottom: 12px; }
  .main-tab {
    padding: 8px 14px;
    border: 1px solid #e5e7eb;
    background: #f9fafb;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 600;
  }
  .main-tab.active {
    background: #e8f0fe;
    border-color: #1a73e8;
    color: #1a73e8;
  }

  /* Language Sub-Tabs */
  .lang-tabs { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
  .lang-tab {
    padding: 6px 10px;
    border: 1px solid #e5e7eb;
    background: #f9fafb;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
  }
  .lang-tab.active {
    background: #e8f0fe;
    border-color: #1a73e8;
    color: #1a73e8;
  }

  /* Crop Checklist Styles */
  .crop-checklist-container {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 1rem;
    background: #f8fafc;
    margin-bottom: 1.25rem;
  }

  .crop-checklist-title {
    font-weight: 600;
    margin-bottom: 12px;
    color: #374151;
    font-size: 0.875rem;
  }

  .crop-checklist-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 12px;
    max-height: 200px;
    overflow-y: auto;
    padding: 8px;
    background: white;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
  }

  .crop-checkbox-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    border-radius: 6px;
    transition: background-color 0.2s;
  }

  .crop-checkbox-item:hover {
    background: #f3f4f6;
  }

  .crop-checkbox {
    width: 16px;
    height: 16px;
    border-radius: 4px;
    border: 2px solid #d1d5db;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
  }

  .crop-checkbox.checked {
    background: #1a73e8;
    border-color: #1a73e8;
  }

  .crop-checkbox.checked::after {
    content: '✓';
    color: white;
    font-size: 12px;
    font-weight: bold;
  }

  .crop-label {
    font-size: 0.875rem;
    color: #374151;
    cursor: pointer;
    user-select: none;
  }

  .selected-crops-count {
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 8px;
  }
    .drawer-content-scroll {
  height: calc(100vh - 40px); /* adjust as needed */
  overflow-y: auto !important;
  padding-right: 10px; /* avoids content cutoff */
}
  .card-body {
  overflow-y: auto !important;
  max-height: 100vh !important;
  padding: 1.5rem !important;
  overscroll-behavior: contain !important;
}
`;


const safeParse = (val) => {
  if (!val) return {};
  if (typeof val === 'object') return val;
  try {
    return JSON.parse(val);
  } catch {
    return {};
  }
};

const parseOtherLangField = (input, fieldKey) => {
  const parsed = safeParse(input);
  const obj = parsed && parsed[fieldKey] ? parsed[fieldKey] : {};
  return typeof obj === 'object' && obj ? obj : {};
};

export default function AddPest({ isOpen, onClose, onSubmit, pestToEdit }) {
  const [message, setMessage] = useState(null);
  const [step, setStep] = useState('basic'); // 'basic' | 'translate' | 'translations'
  const [mainTab, setMainTab] = useState('english'); // 'english' | 'other'
  const [cropSearch, setCropSearch] = useState('');
  const [languages, setLanguages] = useState([]);
  const [selectedLang, setSelectedLang] = useState('');

  // Translations state
  const [translations, setTranslations] = useState({
    PestName: {},
    PestShortName: {},
    Description: {}
  });

  const [images, setImages] = useState([]);
  const [fileObjects, setFileObjects] = useState([]);

  // New state for crops
  const [crops, setCrops] = useState([]);
  const [selectedCropIds, setSelectedCropIds] = useState([]);

  const nameInputRef = useRef(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getValues
  } = useForm({
    defaultValues: {
      name: pestToEdit?.name || '',
      shortName: pestToEdit?.shortName || '',
      description: pestToEdit?.description || '',
      active: pestToEdit?.active !== undefined ? pestToEdit.active : true,
    },
  });

  const selectedLangName = languages.find(l => l.GoogleLanguageCode === selectedLang)?.LanguageName || selectedLang || '';

  const loadCrops = async () => {
    try {
      const cropsData = await getCrops();
      setCrops(cropsData?.data || cropsData || []);
    } catch (error) {
      console.error('Error loading crops:', error);
      setCrops([]);
    }
  };

  const filteredCrops = crops.filter(crop =>
    (crop.CropName || crop.Name || '')
      .toLowerCase()
      .includes(cropSearch.toLowerCase())
  );

  const prefillTranslationsFromEdit = () => {
    if (!pestToEdit || languages.length === 0) return;

    // Parse JSON strings from BE shape in pestToEdit
    const tName = parseOtherLangField(pestToEdit.pestNameOtherLang, 'PestName');
    const tShort = parseOtherLangField(pestToEdit.pestShortNameOtherLang, 'PestShortName');
    const tDesc = parseOtherLangField(pestToEdit.descriptionOtherLang, 'Description');

    const next = { PestName: {}, PestShortName: {}, Description: {} };

    languages.forEach(({ GoogleLanguageCode }) => {
      next.PestName[GoogleLanguageCode] = tName[GoogleLanguageCode] || '';
      next.PestShortName[GoogleLanguageCode] = tShort[GoogleLanguageCode] || '';
      next.Description[GoogleLanguageCode] = tDesc[GoogleLanguageCode] || '';
    });

    setTranslations(next);
  };

  const prefillSelectedCrops = () => {
  if (!pestToEdit?.CropIds) return;

  let cropIds = pestToEdit.CropIds;

  if (typeof cropIds === "string") {
    try {
      cropIds = JSON.parse(cropIds);
    } catch (error) {
      console.error("Invalid CropIds JSON:", cropIds);
      return;
    }
  }

  setSelectedCropIds(cropIds);
};

useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);
  
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => nameInputRef.current && nameInputRef.current.focus(), 100);
      loadCrops();
    }

    if (pestToEdit) {
      setValue('name', pestToEdit.name || '');
      setValue('shortName', pestToEdit.shortName || '');
      setValue('description', pestToEdit.description || '');
      setValue('active', pestToEdit.active !== undefined ? pestToEdit.active : true);

      const loaded = [];
      if (pestToEdit.images?.length > 0) {
        loaded.push({
          id: `existing-${Date.now()}`,
          url: pestToEdit.images[0],
          name: 'Existing Image',
          isExisting: true
        });
      }
      setImages(loaded);
      setFileObjects([]);

      // Prefill selected crops for edit mode
      prefillSelectedCrops();
    } else {
      reset({ name: '', shortName: '', description: '', active: true });
      setImages([]);
      setFileObjects([]);
      setTranslations({ PestName: {}, PestShortName: {}, Description: {} });
      setSelectedCropIds([]);
    }

    setStep('basic');
    setMessage(null);
    setMainTab('english');
    setSelectedLang('');

    if (isOpen) {
      getLanguages()
        .then(res => {
          const list = res?.data || res || [];
          setLanguages(list);
          if (list.length > 0) setSelectedLang(list[0].GoogleLanguageCode);
        })
        .catch(err => {
          console.error('Failed to load languages:', err);
          setLanguages([]);
          setSelectedLang('');
        });
    }
  }, [isOpen]);

  useEffect(() => {
    if (pestToEdit && languages.length > 0) {
      prefillTranslationsFromEdit();
      if (!selectedLang) setSelectedLang(languages[0].GoogleLanguageCode);
    }
  }, [languages]);

  const handleCropToggle = (cropId) => {
    setSelectedCropIds(prev => {
      if (prev.includes(cropId)) {
        return prev.filter(id => id !== cropId);
      } else {
        return [...prev, cropId];
      }
    });
  };

  const handleSelectAllCrops = () => {
    if (selectedCropIds.length === crops.length) {
      setSelectedCropIds([]);
    } else {
      setSelectedCropIds(crops.map(crop => crop.ID));
    }
  };

  const callTranslateApi = async ({ PestName, PestShortName, Description }) => {
    try {
      const res = await translateData({
        PestName,
        PestShortName,
        Description
      });

      const maybeData = res?.data ?? res ?? {};
      const data = maybeData?.data && (maybeData.PestName === undefined)
        ? maybeData.data
        : maybeData;

      const normalized = { PestName: {}, PestShortName: {}, Description: {} };

      if (languages.length > 0) {
        languages.forEach(({ GoogleLanguageCode }) => {
          normalized.PestName[GoogleLanguageCode] =
            data.PestName?.[GoogleLanguageCode] || data.PestName?.en || PestName;

          normalized.PestShortName[GoogleLanguageCode] =
            data.PestShortName?.[GoogleLanguageCode] || data.PestShortName?.en || PestShortName;

          normalized.Description[GoogleLanguageCode] =
            data.Description?.[GoogleLanguageCode] || data.Description?.en || Description;
        });
      } else {
        normalized.PestName.en = data?.PestName?.en || PestName;
        normalized.PestShortName.en = data?.PestShortName?.en || PestShortName;
        normalized.Description.en = data?.Description?.en || Description;
      }

      return normalized;
    } catch (err) {
      console.error('Translate API error: ', err);

      const fallback = { PestName: {}, PestShortName: {}, Description: {} };
      if (languages.length > 0) {
        languages.forEach(({ GoogleLanguageCode }) => {
          fallback.PestName[GoogleLanguageCode] = PestName;
          fallback.PestShortName[GoogleLanguageCode] = PestShortName;
          fallback.Description[GoogleLanguageCode] = Description;
        });
      } else {
        fallback.PestName.en = PestName;
        fallback.PestShortName.en = PestShortName;
        fallback.Description.en = Description;
      }
      return fallback;
    }
  };

  const onInitialTranslate = async (data) => {
    if (!pestToEdit && fileObjects.length === 0) {
      setMessage({ type: 'error', text: 'Feature Image is required' });
      return;
    }

    if (languages.length === 0) {
      setMessage({ type: 'error', text: 'Languages not loaded yet. Please try again.' });
      return;
    }

    if (selectedCropIds.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one crop' });
      return;
    }

    setStep('translate');

    const translated = await callTranslateApi({
      PestName: data.name,
      PestShortName: data.shortName,
      Description: data.description
    });

    console.log('Translated data:', translated);

    setTranslations(prev => ({
      PestName: { ...prev.PestName, ...translated.PestName },
      PestShortName: { ...prev.PestShortName, ...translated.PestShortName },
      Description: { ...prev.Description, ...translated.Description }
    }));

    setStep('translations');
    setMainTab('other');
  };

  /** -------- Step 2: Final Save -------- */
  const onFinalSave = async () => {
    const name = getValues('name');
    const shortName = getValues('shortName');
    const description = getValues('description');
    const active = getValues('active');

    const payload = {
      PestName: name,
      PestShortName: shortName,
      Description: description,
      IsActive: active,
      CropIds: JSON.stringify(selectedCropIds), // Convert array to string for API
      PestNameOtherLang: { PestName: { ...translations.PestName } },
      PestShortNameOtherLang: { PestShortName: { ...translations.PestShortName } },
      DescriptionOtherLang: { Description: { ...translations.Description } },
      ...(pestToEdit && pestToEdit.id ? { ID: pestToEdit.id } : {})  // ✅ Include ID when updating
    };
    console.log("Final payload:", payload);
    await onSubmit(payload, !!pestToEdit, fileObjects[0] || null);

    setMessage({
      type: 'success',
      text: pestToEdit ? 'Pest updated successfully!' : 'Pest added successfully!'
    });
    setTimeout(() => {
      onClose();
      setMessage(null);
    }, 1200);
  };

  /** -------- Image upload logic -------- */
  const handleImageUpload = (event) => {
    const fileList = event.target.files || event.dataTransfer?.files || [];
    if (!fileList.length) return;

    const file = fileList[0];
    const imgObj = {
      id: Date.now(),
      file,
      url: URL.createObjectURL(file),
      name: file.name,
      size: file.size
    };

    setImages([imgObj]);
    setFileObjects([file]);
  };

  const handleRemoveImage = () => {
    setImages([]);
    setFileObjects([]);
  };
  console.log("pesttoedit", pestToEdit)

  const isTranslationsValid = () => {
    return languages.every((lang) => {
      const code = lang.GoogleLanguageCode;

      return (
        translations.PestName[code]?.trim() &&
        translations.PestShortName[code]?.trim() &&
        translations.Description[code]?.trim()
      );
    });
  };

  return (
    <>
      <style>{customStyles}</style>

      <Drawer open={isOpen} direction="right" size={900}>
        <button
                    variant="outline"
                    onClick={onClose}
                    style={{ border: 'none', color: '#000', padding: 0, textAlign: 'end', padding: '15px', position: 'absolute', top: 0, right: 0 }}
                  >
                    <Icon icon="lucide:x" className="text-2xl" />
                  </button>
        <div className="card-body drawer-content-scroll">
          <h6 className="text-xxl text-primary-light mb-16">
            {pestToEdit ? 'Edit Pest' : 'Add New Pest'}
          </h6>

          {message && (
            <div className={message.type === 'success' ? 'success-message' : 'error-message'}>
              {message.text}
            </div>
          )}

          {/* Main Tabs: English / Other Languages */}
          <div className="main-tabs">
            <button
              type="button"
              className={`main-tab ${mainTab === 'english' ? 'active' : ''}`}
              onClick={() => setMainTab('english')}
            >
              English
            </button>
            <button
              type="button"
              className={`main-tab ${mainTab === 'other' ? 'active' : ''}`}
              onClick={() => setMainTab('other')}
              disabled={!pestToEdit && step !== 'translations'}
              title={!pestToEdit && step !== 'translations' ? 'Translate first to enable' : 'Other languages'}
            >
              Other Languages
            </button>
          </div>

          {/* Translating loader */}
          {step === 'translate' && (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <h4>Translating…</h4>
              <p>Please wait...</p>
            </div>
          )}

          {/* ENGLISH TAB */}
          {mainTab === 'english' && step !== 'translate' && (
            <form onSubmit={handleSubmit(onInitialTranslate)}>
              {/* Name + Short Name */}
              <div className="form-grid-container">
                <div>
                  <label className="form-label">Pest Name *</label>
                  <input
                    className="form-control"
                    {...register('name', { required: 'Required', minLength: { value: 2, message: 'Min 2 chars' } })}
                    ref={(e) => {
                      register('name').ref(e);
                      nameInputRef.current = e;
                    }}
                  />
                  {errors.name && <span className="error-message">{errors.name.message}</span>}
                </div>

                <div>
                  <label className="form-label">Short Name *</label>
                  <input
                    className="form-control"
                    {...register('shortName', { required: 'Required', pattern: { value: /^[A-Za-z0-9]+$/, message: 'Only alphanumeric characters allowed' } })}
                  />
                  {errors.shortName && <span className="error-message">{errors.shortName.message}</span>}
                </div>
              </div>

              {/* Description */}
              <div className="mb-20">
                <label className="form-label">Description *</label>
                <textarea
                  className="form-control"
                  {...register('description', { required: 'Required', maxLength: { value: 500, message: 'Max 500 chars' } })}
                />
                {errors.description && <span className="error-message">{errors.description.message}</span>}
              </div>

              {/* Crop Checklist */}
              <div className="crop-checklist-container">
                <div className="crop-checklist-title"
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>

                  <span>Select Crops *</span>

                  <button
                    type="button"
                    onClick={handleSelectAllCrops}
                    style={{
                      padding: '4px 8px',
                      fontSize: '0.75rem',
                      background: '#f3f4f6',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    {selectedCropIds.length === crops.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>

                <input
                  type="text"
                  placeholder="Search crops..."
                  value={cropSearch}
                  onChange={(e) => setCropSearch(e.target.value)}
                  className="form-control"
                  style={{ marginBottom: '10px' }}
                />


                <div className="crop-checklist-grid">
                  {filteredCrops.map((crop) => (
                    <div key={crop.ID} className="crop-checkbox-item">
                      <div
                        className={`crop-checkbox ${selectedCropIds.includes(crop.ID) ? 'checked' : ''}`}
                        onClick={() => handleCropToggle(crop.ID)}
                      />
                      <label
                        className="crop-label"
                        onClick={() => handleCropToggle(crop.ID)}
                      >
                        {crop.CropName || crop.Name || `Crop ${crop.ID}`}
                      </label>
                    </div>
                  ))}
                </div>

                <div className="selected-crops-count">
                  {selectedCropIds.length} of {crops.length} crops selected
                </div>

                {selectedCropIds.length === 0 && (
                  <span className="error-message">Please select at least one crop</span>
                )}
              </div>

              {/* Feature Image + Status */}
              <div className="form-grid-container">
                <div>
                  <label className="form-label">
                    Feature Image {!pestToEdit && <span className="text-danger-600">*</span>}
                  </label>
                  <div className="image-upload" onClick={() => document.getElementById('imgInput')?.click()}>
                    <input
                      id="imgInput"
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                    />
                    {images.length === 0 ? 'Click to upload' : 'Click to change image'}
                  </div>

                  {images.length > 0 && (
                    <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <img src={`https://api.soksan-dss.com${images[0].url}`} width={80} style={{ borderRadius: 6 }} alt="feature" />
                      <button type="button" className="remove-image-btn" onClick={handleRemoveImage}>×</button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="form-label">Status</label>
                  <div className="form-check style-check d-flex align-items-center gap-8">
                    <input
                      className="form-check-input radius-4 border border-neutral-400"
                      type="checkbox"
                      {...register('active')}
                    />
                    <label className="form-label fw-medium text-primary-light text-sm mb-0">
                      Active
                    </label>
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="d-flex justify-content-center gap-3">
                <button type="button" className="border border-danger-600 text-danger-600 px-56 py-10 radius-8" onClick={onClose}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-yellow px-56 py-12"
                  title={(!pestToEdit && images.length === 0) || selectedCropIds.length === 0 ?
                    (!pestToEdit && images.length === 0 ? 'Please add a Feature Image first' : 'Please select at least one crop') :
                    'Translate'}
                  disabled={(!pestToEdit && images.length === 0) || selectedCropIds.length === 0}
                >
                  Translate
                </button>
              </div>
            </form>
          )}

          {/* OTHER LANGUAGES TAB */}
          {mainTab === 'other' && (
            <>
              {/* Language Sub-Tabs */}
              <div className="lang-tabs">
                {languages.length === 0 ? (
                  <span style={{ opacity: 0.7 }}>No languages loaded</span>
                ) : (
                  languages.map((lang) => (
                    <button
                      key={lang.GoogleLanguageCode}
                      type="button"
                      className={`lang-tab ${selectedLang === lang.GoogleLanguageCode ? 'active' : ''}`}
                      onClick={() => setSelectedLang(lang.GoogleLanguageCode)}
                    >
                      {lang.LanguageName}
                    </button>
                  ))
                )}
              </div>

              {/* Translation Fields */}
              {selectedLang ? (
                <>
                  <div className="form-grid-container">
                    <div>
                      <label className="form-label">Pest Name ({selectedLangName})</label>
                      <input
                        className="form-control"
                        value={translations.PestName[selectedLang] || ''}
                        onChange={(e) =>
                          setTranslations((prev) => ({
                            ...prev,
                            PestName: { ...prev.PestName, [selectedLang]: e.target.value }
                          }))
                        }
                      />
                    </div>

                    <div>
                      <label className="form-label">Short Name ({selectedLangName})</label>
                      <input
                        className="form-control"
                        value={translations.PestShortName[selectedLang] || ''}
                        onChange={(e) =>
                          setTranslations((prev) => ({
                            ...prev,
                            PestShortName: { ...prev.PestShortName, [selectedLang]: e.target.value }
                          }))
                        }
                      />
                    </div>
                  </div>

                  <div className="mb-20">
                    <label className="form-label">Description ({selectedLangName})</label>
                    <textarea
                      className="form-control"
                      value={translations.Description[selectedLang] || ''}
                      onChange={(e) =>
                        setTranslations((prev) => ({
                          ...prev,
                          Description: { ...prev.Description, [selectedLang]: e.target.value }
                        }))
                      }
                    />
                  </div>
                </>
              ) : (
                <div style={{ opacity: 0.7, marginBottom: 12 }}>Select a language</div>
              )}

              {/* Buttons */}
              <div className="d-flex justify-content-center gap-3">
                <button type="button" className="border border-danger-600 text-danger-600 px-56 py-10 radius-8" onClick={onClose}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-yellow px-56 py-12"
                  onClick={onFinalSave}
                  disabled={!isTranslationsValid()}
                  title={!isTranslationsValid() ? "Please fill all fields for all languages" : ""}
                  style={{ opacity: !isTranslationsValid() ? 0.5 : 1 }}
                >
                  Save
                </button>

              </div>
            </>
          )}
        </div>
      </Drawer>
    </>
  );
}